import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Modal, Row, Table } from "react-bootstrap";
import { FaCrown, FaEdit, FaPlus, FaShoppingCart, FaTrash } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import { useTable } from "react-table";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BASE_URL = process.env.REACT_APP_API_URL;


const IndividualDepartment = () => {
  const [departments, setDepartments] = useState([]);
  const [departmentName, setDepartmentName] = useState("");
  const [description, setDescription] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [departmentLimitReached, setDepartmentLimitReached] = useState(false);
  const [departmentLimitData, setDepartmentLimitData] = useState({ departmentCount: 0, departmentQty: 0 });
  const navigate = useNavigate();

   const notify = (message, type = 'success') => toast[type](message, {
     position: "top-right",
     autoClose: 3000
   });

  const checkDepartmentLimit = async () => {
    try {
      const response = await fetch(`${BASE_URL}/individual-department-limit?BranchCode=${BranchCode}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to check department limit');
      }

      setDepartmentLimitData(data);
      setDepartmentLimitReached(data.departmentCount >= data.departmentQty);

      return {
        hasReachedLimit: data.departmentCount >= data.departmentQty,
        current: data.departmentCount,
        limit: data.departmentQty
      };
    } catch (error) {
      console.error('Error checking department limit:', error);
      notify('Failed to check department limit', 'error');
      return { hasReachedLimit: true };
    }
  };

  useEffect(() => {
    if (message || error) {
      const timeout = setTimeout(() => {
        setMessage("");
        setError("");
      }, 1500);

      return () => clearTimeout(timeout);
    }
  }, [message, error]);

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const userEmail = userData?.email;
  const BranchCode = userData?.BranchCode;

  const fetchDepartmentData = useCallback(async () => {
    if (!BranchCode) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/get-departments?BranchCode=${BranchCode}`);
      const data = await response.json();

      if (response.ok) {
        setDepartments(data);
        await checkDepartmentLimit();
      } else {
        notify("Error fetching departments.", "error");
      }
    } catch (error) {
      console.error("Error fetching department data:", error);
      notify("Error fetching department data. Please check your connection.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [BranchCode]);

  useEffect(() => {
    fetchDepartmentData();
  }, [fetchDepartmentData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "departmentName") setDepartmentName(value);
    if (name === "description") setDescription(value);
  };

  const resetForm = () => {
    setDepartmentName("");
    setDescription("");
    setIsEditing(false);
    setEditId(null);
    setError("");
    setMessage("");
  };

  const openCreateModal = async () => {
    if (departmentLimitReached) {
      setShowPurchaseModal(true);
    } else {
      resetForm();
      setShowModal(true);
    }
  };

  const handleBuyNow = () => {
    setShowPurchaseModal(false);
    navigate('/pricing', { 
      state: { 
        from: 'department',
        BranchCode: BranchCode
      } 
    });
  };

  const handleCreateDepartment = async () => {
    if (!departmentName || !description) {
      notify('Please fill in all fields', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/add-department`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ departmentName, description, emailAddress: userEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        notify('Department created successfully');
        resetForm();
        setShowModal(false);
        fetchDepartmentData();
      } else {
        if (data.error === "Department creation limit reached") {
          setShowModal(false);
          setShowPurchaseModal(true);
        } else {
          notify(data.error || "Error creating department", 'error');
        }
      }
    } catch (error) {
      console.error("Error creating department:", error);
      notify('Failed to create department', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (department) => {
    setIsEditing(true);
    setEditId(department.id);
    setDepartmentName(department.departmentName);
    setDescription(department.description);
    setShowModal(true);
  };

  const handleUpdateDepartment = async () => {
    if (!departmentName || !description) {
      notify("Please fill in all fields", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/update-department/${editId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ departmentName, description }),
      });

      const data = await response.json();

      if (response.ok) {
        notify("Department updated successfully");
        resetForm();
        setShowModal(false);
        fetchDepartmentData();
      } else {
        notify(data.error || "Error updating department", "error");
      }
    } catch (error) {
      console.error("Error updating department:", error);
      notify("Error updating department. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/delete-department/${departmentToDelete}`, {
        method: "DELETE",
      });
      const data = await response.json();
      
      if (response.ok) {
        notify(data.message || "Department deleted successfully!");
        fetchDepartmentData();
        setShowDeleteModal(false);
      } else {
        notify(data.error || "Error deleting department", "error");
        setShowDeleteModal(false);
      }
    } catch (error) {
      console.error("Error deleting department:", error);
      notify("Error deleting department. Please try again.", "error");
      setShowDeleteModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Department Name",
        accessor: "departmentName",
      },
      {
        Header: "Description",
        accessor: "description",
        Cell: ({ value }) => (
          <div className="description-cell">
            {window.innerWidth < 768 && value.length > 50
              ? `${value.substring(0, 50)}...`
              : value}
          </div>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2 justify-content-center">
            <Button 
              variant="outline-warning" 
              size="sm" 
              onClick={() => handleEdit(row.original)}
              disabled={isLoading}
              className="d-flex align-items-center"
            >
              <FaEdit className="me-1" /> Edit
            </Button>
            <Button 
              variant="outline-danger" 
              size="sm" 
              onClick={() => {
                setDepartmentToDelete(row.original.id);
                setShowDeleteModal(true);
              }}
              disabled={isLoading}
              className="d-flex align-items-center"
            >
              <FaTrash className="me-1" /> Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: departments,
  });

  return (
    <Container fluid className="py-3">
      <ToastContainer />

      <Row className="justify-content-center">
        <Col xs={12} lg={10} xl={9}>
          <Card className="shadow-lg border-0 rounded-lg mb-4">
            <Card.Header className="bg-primary text-white py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-0 fs-4">Your Departments</h3>
                  <p className="text-white-50 mb-0 d-none d-sm-block">
                    Manage your departments {departmentLimitData.departmentQty > 0 && `(${departmentLimitData.departmentCount}/${departmentLimitData.departmentQty})`}
                  </p>
                </div>
                <Button 
                  variant={departmentLimitReached ? "warning" : "light"}
                  className="d-flex align-items-center" 
                  onClick={openCreateModal}
                  disabled={isLoading}
                >
                  {departmentLimitReached ? (
                    <>
                      <FaCrown className="me-2" />
                      <span className="d-none d-sm-inline">Upgrade to Add</span>
                      <span className="d-sm-none">Upgrade</span>
                    </>
                  ) : (
                    <>
                      <FaPlus className="me-2" />
                      <span className="d-none d-sm-inline">Add Department</span>
                      <span className="d-sm-none">Add</span>
                    </>
                  )}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="px-2 px-sm-3 px-md-4">
              {/* Enhanced Loading indicator */}
              {isLoading && (
                <div className="text-center py-5">
                  <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-primary fw-bold mb-1">Loading Departments</p>
                  <p className="text-muted">Please wait while we fetch your department data...</p>
                </div>
              )}

              {/* Mobile view - Only show when not loading */}
              {!isLoading && (
                <div className="d-md-none">
                  {departments.length === 0 ? (
                    <p className="text-center py-3">No departments found. Add your first department.</p>
                  ) : (
                    departments.map((department) => (
                      <Card key={department.id} className="mb-3 department-card">
                        <Card.Body>
                          <Card.Title>{department.departmentName}</Card.Title>
                          <Card.Text>{department.description}</Card.Text>
                          <div className="d-flex justify-content-end gap-2">
                            <Button
                              variant="warning"
                              size="sm"
                              onClick={() => handleEdit(department)}
                              aria-label="Edit department"
                            >
                              <FaEdit /> Edit
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => {
                                setDepartmentToDelete(department.id);
                                setShowDeleteModal(true);
                              }}
                              aria-label="Delete department"
                            >
                              <FaTrash /> Delete
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    ))
                  )}
                </div>
              )}

              {/* Desktop view - Only show when not loading */}
              {!isLoading && (
                <div className="table-responsive d-none d-md-block">
                  <Table striped bordered hover responsive {...getTableProps()}>
                    <thead>
                      {headerGroups.map((headerGroup, idx) => (
                        <tr key={idx} {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map((column, colIdx) => (
                            <th key={colIdx} {...column.getHeaderProps()}>
                              {column.render("Header")}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="text-center py-3">
                            No departments found. Add your first department.
                          </td>
                        </tr>
                      ) : (
                        rows.map((row, rowIdx) => {
                          prepareRow(row);
                          return (
                            <tr key={rowIdx} {...row.getRowProps()}>
                              {row.cells.map((cell, cellIdx) => (
                                <td key={cellIdx} {...cell.getCellProps()}>
                                  {cell.render("Cell")}
                                </td>
                              ))}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal 
        show={showModal} 
        onHide={() => {
          setShowModal(false);
          resetForm();
        }} 
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Edit Department" : "Add New Department"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="py-2">{error}</Alert>}
          <Form>
            <Form.Group controlId="departmentName">
              <Form.Label>Department Name</Form.Label>
              <Form.Control
                type="text"
                name="departmentName"
                placeholder="Enter department name"
                value={departmentName}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group controlId="description" className="mt-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                rows={3}
                placeholder="Enter department description"
                value={description}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowModal(false);
              resetForm();
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={isEditing ? handleUpdateDepartment : handleCreateDepartment}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
              </>
            ) : (
              isEditing ? 'Update Department' : 'Create Department'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal 
        show={showDeleteModal} 
        onHide={() => setShowDeleteModal(false)} 
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this department? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>Deleting...</span>
              </>
            ) : (
              'Delete Department'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showPurchaseModal}
        onHide={() => setShowPurchaseModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Upgrade Your Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-3">
            <FaShoppingCart className="text-primary" size={50} />
          </div>
          <h4 className="text-center mb-3">Department Creation Limit Reached</h4>
          <p className="text-center">
            You've reached the maximum number of departments allowed in your current plan. 
            Upgrade now to create more departments and access premium features.
          </p>
          <div className="bg-light p-3 rounded mt-3">
            <h5>Premium Plan Benefits:</h5>
            <ul className="mt-2">
              <li>Additional department creation</li>
              <li>Advanced department management</li>
              <li>Priority support</li>
              <li>Enhanced customization options</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowPurchaseModal(false)}
          >
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleBuyNow}
            className="d-flex align-items-center"
          >
            <FaShoppingCart className="me-2" /> Upgrade Plan
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .description-cell {
          max-width: 250px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        @media (max-width: 768px) {
          .department-card {
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            transition: all 0.3s ease;
          }
          
          .department-card:hover {
            box-shadow: 0 4px 8px rgba(0,0,0,0.2);
          }
        }
        
        /* Enhanced loading animation */
        .spinner-grow {
          animation-duration: 1.5s;
        }
        
        .text-primary.fw-bold {
          font-size: 1.2rem;
        }
        
        /* Fadeout animation for when loading completes */
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        .loading-fadeout {
          animation: fadeOut 0.5s ease-out forwards;
        }
      `}</style>
    </Container>
  );
};

export default IndividualDepartment;